import { reducerWithInitialState } from 'typescript-fsa-reducers/dist';
import { CardSettingsPayload, DashboardLoadPayload, dashboardSettingsLoaded, generateReport, hideReport, saveCardSettings, setCardType } from '../actions/teacher-ui-actions';
import { ReducerFn } from '../../../../its-pages/src/utils/redux/reducers/types';
import { CardElementType, CardIdToSelectionMap } from '../../types/cards';
import { GenerateExamplePayload, PickExamplePayload, SaveNewExampleSelectionPayload, generateExampleFromEventId, pickThenLoadExampleFromEventIds, saveNewExampleSelectionInformation, setCurrentCompetencyId } from '../actions/statistics';

type TeacherUiReducer<P> = ReducerFn<TeacherUIState, P>;

type CachedExampleParams = {
  cardId: string;
  eventIds: string[];

  /** If true, displayed example is exactly a user mistake. Else it is a generated problem */
  isShowingUserExample: boolean;
  tagIds: string[];
  showUsername: boolean;
}

export interface TeacherUIState {
  reportOpen: boolean;
  /** Minimum number of unique users for a card to be shown */
  cardMinUsers: number;
  /** Value between 0-100 for min percentage of confusion type cards for it to be shown */
  cardMinPercentage: number;
  cardsToDisplay: CardElementType[] | null;
  cardIdToSelectionInfo: CardIdToSelectionMap;
  cachedGeneratedExampleParams: CachedExampleParams;
}

const INITIAL_STATE: TeacherUIState = {
  reportOpen: false,
  cardMinUsers: 3,
  cardMinPercentage: 5,
  cardsToDisplay: null,
  cardIdToSelectionInfo: {},
  cachedGeneratedExampleParams: {
    cardId: '',
    eventIds: [],
    isShowingUserExample: false,
    tagIds: [],
    showUsername: false
  }
};

const dashboardSettingsLoadedReducer: TeacherUiReducer<DashboardLoadPayload> = (
  state,
  { cards, cardSettings: { minPercent, minUsers} }
) => {
  return { ...state, cardIdToSelectionInfo: cards, cardMinUsers: minUsers, cardMinPercentage: minPercent };
};

const saveCardSettingsReducer: TeacherUiReducer<CardSettingsPayload> = (state, {minPercent, minUsers}) => {
  return {
    ...state,
    cardMinPercentage: minPercent,
    cardMinUsers: minUsers
  };
};

const generateReportReducer: TeacherUiReducer<CardElementType[]> = (state, payload) => {
  return {
    ...state,
    reportOpen: true,
    cardsToDisplay: payload
  };
};

const hideReportReducer: TeacherUiReducer<any> = (state) => {
  return {
    ...state,
    reportOpen: false,
    cardsToDisplay: null
  };
};

const saveNewExampleSelectionReducer: TeacherUiReducer<SaveNewExampleSelectionPayload> = (state, payload) => {
  const { cardId, generatorSeed, lastEventId } = payload;
  const existingCardExampleData = state.cardIdToSelectionInfo[cardId];
  if (existingCardExampleData) {
    existingCardExampleData.generatorSeed = generatorSeed ?? existingCardExampleData.generatorSeed;
    existingCardExampleData.lastEventId = lastEventId ?? existingCardExampleData.lastEventId;
  } else {
    state.cardIdToSelectionInfo[cardId] = {
      generatorSeed: generatorSeed ?? 1,
      lastEventId: lastEventId ?? ''
    }
  }
  return state;
};

const pickRandomExistingUserMistakeInCard: TeacherUiReducer<PickExamplePayload> = (state, payload) => {
  const { cardId, eventIds, showAnswers, showUsername } = payload;
  const existingData = state.cachedGeneratedExampleParams;
  state.cachedGeneratedExampleParams = {
    ...existingData,
    cardId,
    eventIds,
    isShowingUserExample: true,
    showUsername
  };
  return state;
}

const generateNewExampleInCard: TeacherUiReducer<GenerateExamplePayload> = (state, payload) => {
  const { cardId, eventIds, tagIds } = payload;
  const existingData = state.cachedGeneratedExampleParams;
  state.cachedGeneratedExampleParams = {
    ...existingData,
    cardId,
    eventIds,
    isShowingUserExample: false,
    tagIds
  };
  return state;
};

export const teacherUIReducers = reducerWithInitialState(INITIAL_STATE)
  .case(dashboardSettingsLoaded, dashboardSettingsLoadedReducer)
  .case(saveCardSettings, saveCardSettingsReducer)
  .case(generateReport, generateReportReducer)
  .cases([hideReport, setCardType, setCurrentCompetencyId], hideReportReducer)
  .case(saveNewExampleSelectionInformation, saveNewExampleSelectionReducer)
  .case(pickThenLoadExampleFromEventIds, pickRandomExistingUserMistakeInCard)
  .case(generateExampleFromEventId, generateNewExampleInCard)
  .build();
